import {
  cloneElement,
  FC,
  isValidElement,
  ReactElement,
  ReactNode,
  Ref,
  TextareaHTMLAttributes
} from 'react';
import clsx from 'clsx';
import { useGetThemeConfig } from '@namespace/themes';
import { propsMerge } from '../../utils/propsMerge';
import { Box } from '../Box';

import styles from './index.module.css';

type ResizeOption = 'none' | 'vertical' | 'horizontal';
type IntentOption = 'default' | 'error' | 'success';
type SizeOption = 's' | 'm';

export interface TextAreaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  inputRef?: Ref<HTMLTextAreaElement>;
  size?: SizeOption;
  className?: string;
  wrapperClassName?: string;
  resize?: ResizeOption;
  intent?: IntentOption;
  isFullFieldBorder?: boolean;
  bottomElement?: ReactNode;
}

const getResizeCls = (resize: ResizeOption) =>
  resize ? styles[`resize${resize[0].toUpperCase() + resize.slice(1)}`] : '';

export const TextArea: FC<TextAreaProps> = ({
  inputRef,
  className = '',
  resize = 'none',
  size = 's',
  wrapperClassName = '',
  intent = 'default',
  ...rest
}) => {
  const textAreaConfig = useGetThemeConfig('TextArea') || {};
  const {
    bottomElement = null,
    isFullFieldBorder = false,
    ...mergedProps
  } = propsMerge(rest, textAreaConfig);
  return (
    <Box className={clsx(wrapperClassName, styles[size], styles[intent])}>
      <textarea
        ref={inputRef}
        className={clsx(
          styles.textarea,
          className,
          getResizeCls(resize),
          isFullFieldBorder ? styles.fullBorder : ''
        )}
        {...mergedProps}
      />
      {isValidElement(bottomElement) &&
        cloneElement(bottomElement as ReactElement, {
          className: clsx(
            (bottomElement as ReactElement).props.className,
            styles.borderBottomCustom
          )
        })}
    </Box>
  );
};
